import axios from 'axios';

// API Base URL
const baseURL = process.env.REACT_APP_API_URL || '/api';

// Create axios instance with credentials
const api = axios.create({
  baseURL,
  timeout: 10000,
  withCredentials: true, // IMPORTANT: Send cookies with requests
  headers: {
    'Content-Type': 'application/json',
  }
});

// Request interceptor - no token needed, cookies handled automatically
api.interceptors.request.use(
  config => {
    // Propagation de l'impersonation (Vue en tant que — admin DEV uniquement)
    // window.__suiviplus_impersonate est défini par ImpersonateContext.
    // Le backend ne l'utilise QUE si l'user réel est admin (sécurité côté serveur).
    const imp = typeof window !== 'undefined' && window.__suiviplus_impersonate;
    if (imp) {
      config.headers = config.headers || {};
      if (imp.role) config.headers['X-Impersonate-Role'] = imp.role;
      if (Array.isArray(imp.modules)) {
        config.headers['X-Impersonate-Modules'] = JSON.stringify(imp.modules);
      } else if (typeof imp.modules === 'string') {
        config.headers['X-Impersonate-Modules'] = imp.modules;
      }
      if (Array.isArray(imp.structures)) {
        config.headers['X-Impersonate-Structures'] = JSON.stringify(
          imp.structures.map(s => s.structureId || s.id || s)
        );
      }
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Response interceptor for global error handling
api.interceptors.response.use(
  response => response,
  error => {
    // Handle authentication errors
    if (error.response && error.response.status === 401) {
      // User not authenticated, redirect to login
      console.warn('Unauthorized access, redirecting to login...');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// Authentication endpoints
export const checkSession = () => api.get('/auth/session');
export const getProfile = () => api.get('/auth/profile');
export const logout = () => api.post('/auth/logout');
export const getAllUsers = () => api.get('/admin/users'); // Admin endpoint for user management

// Products endpoints
export const getProducts = (structureId) =>
  api.get(`/products?structureId=${structureId}`);

export const getProductById = (id, structureId) =>
  api.get(`/products/${id}?structureId=${structureId}`);

export const addProduct = (product) =>
  api.post('/products', product);

export const updateProduct = (id, product) =>
  api.put(`/products/${id}`, product);

export const deleteProduct = (id, structureId) =>
  api.delete(`/products/${id}?structureId=${structureId}`);

export const getProductsMultiStructures = (structureIds) =>
  api.get(`/products/multi-structures?structureIds=${structureIds.join(',')}`);

export const searchProductsInOtherStructures = (name, excludeStructureId) =>
  api.get(`/products/search-other?name=${encodeURIComponent(name)}&excludeStructureId=${excludeStructureId}`);

export const uploadProductFile = (productId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/products/${productId}/file`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const deleteProductFile = (productId) =>
  api.delete(`/products/${productId}/file`);

export const updateStock = (id, data) => {
  console.log("Updating stock for product:", id, data);
  return api.patch(`/products/${id}/stock`, data);
};

export const getShoppingList = () =>
  api.get('/products/shopping-list');

// Categories endpoints
export const getCategories = (structureId) => {
  console.log("Fetching categories for structure:", structureId);
  return api.get(`/categories?structureId=${structureId}`);
};

export const addCategory = (category) =>
  api.post('/categories', category);

export const updateCategory = (id, category) =>
  api.put(`/categories/${id}`, category);

export const deleteCategory = (id, structureId) =>
  api.delete(`/categories/${id}?structureId=${structureId}`);

// Structures endpoints
export const getStructures = () =>
  api.get('/structures');

export const getUserStructures = () =>
  api.get('/structures/user');

export const addStructure = (structure) =>
  api.post('/structures', structure);

export const createStructure = (structure) =>
  api.post('/structures', structure);

export const updateStructure = (id, structure) =>
  api.put(`/structures/${id}`, structure);

export const deleteStructure = (id) =>
  api.delete(`/structures/${id}`);

// Admin - User management endpoints
export const createUser = (userData) =>
  api.post('/admin/users', userData);

export const updateUser = (userId, userData) =>
  api.put(`/admin/users/${userId}`, userData);

export const deleteUser = (userId) =>
  api.delete(`/admin/users/${userId}`);

// People endpoints
export const getPeople = (structureId) =>
  api.get(`/people?structureId=${structureId}`);

export const addPerson = (person) =>
  api.post('/people', person);

export const updatePerson = (id, person) =>
  api.put(`/people/${id}`, person);

export const deletePerson = (id) =>
  api.delete(`/people/${id}`);

// Suppliers endpoints
export const getSuppliers = (structureId) =>
  api.get(`/suppliers?structureId=${structureId}`);

export const addSupplier = (supplier) =>
  api.post('/suppliers', supplier);

export const updateSupplier = (id, supplier) =>
  api.put(`/suppliers/${id}`, supplier);

export const deleteSupplier = (id, structureId) =>
  api.delete(`/suppliers/${id}?structureId=${structureId}`);

export const getSupplierStats = (id, structureId) =>
  api.get(`/suppliers/${id}/stats?structureId=${structureId}`);

// Price management endpoints
export const getPriceHistory = (productId, params = {}) =>
  api.get(`/products/${productId}/prices`, { params });

export const addPriceEntry = (productId, priceData) =>
  api.post(`/products/${productId}/prices`, priceData);

export const updateProductSupplier = (productId, data) =>
  api.put(`/products/${productId}/supplier`, data);

export const getPriceComparison = (productId) =>
  api.get(`/products/${productId}/price-comparison`);

// Dashboard endpoints
export const getDashboardStats = (structureId) =>
  api.get(`/dashboard/stats?structureId=${structureId}`);

export const getPriceTrends = (structureId, days = 90) =>
  api.get(`/dashboard/price-trends?structureId=${structureId}&days=${days}`);

export const getSupplierComparison = (structureId) =>
  api.get(`/dashboard/supplier-comparison?structureId=${structureId}`);

export const getShoppingSuggestions = (structureId) =>
  api.get(`/dashboard/shopping-suggestions?structureId=${structureId}`);

// Orders / Commandes
export const getOrders = (structureIdOrIds, status) => {
  let url;
  if (Array.isArray(structureIdOrIds)) {
    url = `/orders?structureIds=${structureIdOrIds.join(',')}`;
  } else {
    url = `/orders?structureId=${structureIdOrIds}`;
  }
  if (status) url += `&status=${status}`;
  return api.get(url);
};

export const getOrderById = (id) =>
  api.get(`/orders/${id}`);

export const createOrder = (orderData) =>
  api.post('/orders', orderData);

export const receiveOrder = (id, data) =>
  api.patch(`/orders/${id}/receive`, data);

export const closeOrder = (id) =>
  api.patch(`/orders/${id}/close`);

export const cancelOrder = (id) =>
  api.patch(`/orders/${id}/cancel`);

// ============================================
// STOCK MOVEMENTS
// ============================================

export const getDailyMovements = (structureId) =>
  api.get(`/stock-movements/today?structureId=${structureId}`);

export const getMovementsByDate = (structureId, date) =>
  api.get(`/stock-movements/by-date?structureId=${structureId}&date=${date}`);

export const getMovementStats = (structureId) =>
  api.get(`/stock-movements/stats?structureId=${structureId}`);

// ============================================
// MILK TRACEABILITY
// ============================================

export const getMilkBatches = (structureId, showExpired = false) =>
  api.get(`/milk-batches?structureId=${structureId}&showExpired=${showExpired}`);

export const createMilkBatch = (data) =>
  api.post('/milk-batches', data);

export const updateMilkBatch = (id, data) =>
  api.put(`/milk-batches/${id}`, data);

export const deleteMilkBatch = (id) =>
  api.delete(`/milk-batches/${id}`);

export const checkMilkRecall = (lotNumber) =>
  api.post('/milk-batches/check-recall', { lotNumber });

export const openMilkBox = (id, data) =>
  api.patch(`/milk-batches/${id}/open`, data);
export const closeMilkBox = (id) => api.patch(`/milk-batches/${id}/close`);

export const getMilkBatchesMultiStructures = (structureIds) =>
  api.get(`/milk-batches/multi-structures?structureIds=${structureIds.join(',')}`);

// Milk brands (admin)
export const getMilkBrands = () => api.get('/milk-brands');
export const createMilkBrand = (data) => api.post('/milk-brands', data);
export const updateMilkBrand = (id, data) => api.put(`/milk-brands/${id}`, data);
export const deleteMilkBrand = (id) => api.delete(`/milk-brands/${id}`);

// Children (traçabilité lait — prénoms par structure)
export const getChildren = (structureId) => api.get(`/children?structureId=${structureId}`);
export const createChild = (data) => api.post('/children', data);
export const updateChild = (id, data) => api.put(`/children/${id}`, data);
export const deleteChild = (id) => api.delete(`/children/${id}`);

// ============================================
// MED TRACEABILITY
// ============================================

export const getMedProductTypes = () => api.get('/med-batches/product-types');
export const createMedProductType = (data) => api.post('/med-batches/product-types', data);
export const updateMedProductType = (id, data) => api.put(`/med-batches/product-types/${id}`, data);
export const deleteMedProductType = (id) => api.delete(`/med-batches/product-types/${id}`);

export const getMedBatches = (structureId, showExpired = false) =>
  api.get(`/med-batches?structureId=${structureId}&showExpired=${showExpired}`);
export const createMedBatch = (data) => api.post('/med-batches', data);
export const updateMedBatch = (id, data) => api.put(`/med-batches/${id}`, data);
export const deleteMedBatch = (id) => api.delete(`/med-batches/${id}`);
export const checkMedRecall = (lotNumber) => api.post('/med-batches/check-recall', { lotNumber });
export const openMedBox = (id, data) => api.patch(`/med-batches/${id}/open`, data);
export const closeMedBox = (id) => api.patch(`/med-batches/${id}/close`);

export const getMedBatchesMultiStructures = (structureIds) =>
  api.get(`/med-batches/multi-structures?structureIds=${structureIds.join(',')}`);

// ============================================
// WATER TRACEABILITY
// ============================================

export const getWaterProductTypes = () => api.get('/water-batches/product-types');
export const createWaterProductType = (data) => api.post('/water-batches/product-types', data);
export const updateWaterProductType = (id, data) => api.put(`/water-batches/product-types/${id}`, data);
export const deleteWaterProductType = (id) => api.delete(`/water-batches/product-types/${id}`);

export const getWaterBatches = (structureId, showExpired = false) =>
  api.get(`/water-batches?structureId=${structureId}&showExpired=${showExpired}`);
export const createWaterBatch = (data) => api.post('/water-batches', data);
export const updateWaterBatch = (id, data) => api.put(`/water-batches/${id}`, data);
export const deleteWaterBatch = (id) => api.delete(`/water-batches/${id}`);
export const checkWaterRecall = (lotNumber) => api.post('/water-batches/check-recall', { lotNumber });
export const openWaterBox = (id, data) => api.patch(`/water-batches/${id}/open`, data);
export const closeWaterBox = (id) => api.patch(`/water-batches/${id}/close`);

export const getWaterBatchesMultiStructures = (structureIds) =>
  api.get(`/water-batches/multi-structures?structureIds=${structureIds.join(',')}`);

// ============================================
// EQUIPMENT
// ============================================

export const getEquipment = (structureId) =>
  api.get(`/equipment?structureId=${structureId}`);

export const getEquipmentById = (id) =>
  api.get(`/equipment/${id}`);

export const createEquipment = (data) =>
  api.post('/equipment', data);

export const updateEquipment = (id, data) =>
  api.put(`/equipment/${id}`, data);

export const deleteEquipment = (id) =>
  api.delete(`/equipment/${id}`);

export const getEquipmentMultiStructures = (structureIds) =>
  api.get(`/equipment/multi-structures?structureIds=${structureIds.join(',')}`);

export const getEquipmentCategories = () =>
  api.get('/equipment-categories');

export const createEquipmentCategory = (data) =>
  api.post('/equipment-categories', data);

export const updateEquipmentCategory = (id, data) =>
  api.put(`/equipment-categories/${id}`, data);

export const deleteEquipmentCategory = (id) =>
  api.delete(`/equipment-categories/${id}`);

export const getEquipmentRepairs = (equipmentId) =>
  api.get(`/equipment/${equipmentId}/repairs`);

export const createEquipmentRepair = (equipmentId, data) =>
  api.post(`/equipment/${equipmentId}/repairs`, data);

export const completeEquipmentRepair = (equipmentId, repairId) =>
  api.patch(`/equipment/${equipmentId}/repairs/${repairId}/complete`);

export const uploadEquipmentFile = (id, fileType, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/equipment/${id}/upload/${fileType}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const deleteEquipmentFile = (id, fileType) =>
  api.delete(`/equipment/${id}/file/${fileType}`);

// ============================================
// MAINTENANCE
// ============================================

export const getMaintenanceInterventions = (structureId) =>
  api.get(`/maintenance?structureId=${structureId}`);

export const createMaintenanceIntervention = (data) =>
  api.post('/maintenance', data);

export const updateMaintenanceIntervention = (id, data) =>
  api.put(`/maintenance/${id}`, data);

export const deleteMaintenanceIntervention = (id) =>
  api.delete(`/maintenance/${id}`);

export const getMaintenanceMultiStructures = (structureIds) =>
  api.get(`/maintenance/multi-structures?structureIds=${structureIds.join(',')}`);

export const getMaintenanceTypes = () =>
  api.get('/maintenance-types');

export const createMaintenanceType = (data) =>
  api.post('/maintenance-types', data);

export const updateMaintenanceType = (id, data) =>
  api.put(`/maintenance-types/${id}`, data);

export const deleteMaintenanceType = (id) =>
  api.delete(`/maintenance-types/${id}`);

export const uploadMaintenanceFile = (id, fileType, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/maintenance/${id}/upload/${fileType}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const deleteMaintenanceFile = (id, fileType) =>
  api.delete(`/maintenance/${id}/file/${fileType}`);

// ============================================
// TRAVAUX
// ============================================

export const getWorkOrders = (structureId) =>
  api.get(`/work-orders?structureId=${structureId}`);

export const getWorkOrdersMultiStructures = (structureIds) =>
  api.get(`/work-orders/multi-structures?structureIds=${structureIds.join(',')}`);

export const getWorkOrderCategories = () => api.get('/work-order-categories');

export const createWorkOrder = (data) => api.post('/work-orders', data);

export const updateWorkOrder = (id, data) => api.put(`/work-orders/${id}`, data);

export const deleteWorkOrder = (id) => api.delete(`/work-orders/${id}`);

export const uploadWorkOrderFile = (id, fileType, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/work-orders/${id}/upload/${fileType}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const deleteWorkOrderFile = (id, fileType) =>
  api.delete(`/work-orders/${id}/file/${fileType}`);

// Work Order Categories (admin)
export const createWorkOrderCategory = (data) => api.post('/work-order-categories', data);
export const updateWorkOrderCategory = (id, data) => api.put(`/work-order-categories/${id}`, data);
export const deleteWorkOrderCategory = (id) => api.delete(`/work-order-categories/${id}`);

// Rooms (pièces / lieux par structure — admin)
export const getRooms = (structureId) => api.get(`/rooms?structureId=${structureId}`);
export const createRoom = (data) => api.post('/rooms', data);
export const updateRoom = (id, data) => api.put(`/rooms/${id}`, data);
export const deleteRoom = (id) => api.delete(`/rooms/${id}`);

// Tasks
export const getTasks = (structureId) => api.get(`/tasks?structureId=${structureId}`);
export const getTasksMultiStructures = (structureIds) =>
  api.get(`/tasks/multi-structures?structureIds=${structureIds.join(',')}`);
export const createTask = (data) => api.post('/tasks', data);
export const updateTask = (id, data) => api.put(`/tasks/${id}`, data);
export const deleteTask = (id) => api.delete(`/tasks/${id}`);

// ============================================
// RH (Module Ressources Humaines)
// ============================================

// Settings LLM + Identité structure
export const getHRSettings = () => api.get('/hr/settings');
export const upsertHRSettings = (data) => api.put('/hr/settings', data);
export const testLLMConnection = () => api.post('/hr/settings/test');

export const getStructureIdentity = (structureId) => api.get(`/hr/structure-identity/${structureId}`);
export const upsertStructureIdentity = (structureId, data) => api.put(`/hr/structure-identity/${structureId}`, data);
export const testStructureSmtp = (structureId) => api.post(`/hr/structure-identity/${structureId}/test-smtp`);
export const uploadStructureLogo = (structureId, formData) => api.post(`/hr/structure-identity/${structureId}/logo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deleteStructureLogo = (structureId) => api.delete(`/hr/structure-identity/${structureId}/logo`);

// Fermetures de structure
export const getStructureClosures = (structureId) => api.get(`/structures/${structureId}/closures`);
export const createStructureClosure = (structureId, data) => api.post(`/structures/${structureId}/closures`, data);
export const updateStructureClosure = (structureId, closureId, data) => api.put(`/structures/${structureId}/closures/${closureId}`, data);
export const deleteStructureClosure = (structureId, closureId, applyToGroup) =>
  api.delete(`/structures/${structureId}/closures/${closureId}`, { params: applyToGroup ? { applyToGroup: 'true' } : {} });

// Dashboard RH
export const getHRDashboard = (structureId) =>
  api.get('/hr/dashboard', { params: structureId ? { structureId } : {} });

// Employés
export const getEmployees = (params) => api.get('/hr/employees', { params });
export const getEmployeeById = (id) => api.get(`/hr/employees/${id}`);
export const createEmployee = (data) => api.post('/hr/employees', data);
export const updateEmployee = (id, data) => api.put(`/hr/employees/${id}`, data);
export const deleteEmployee = (id) => api.delete(`/hr/employees/${id}`);
export const archiveEmployee = (id) => api.patch(`/hr/employees/${id}/archive`);
export const updateEmployeeSecondaryStructures = (id, structureIds) =>
  api.put(`/hr/employees/${id}`, { secondaryStructureIds: structureIds });

// Templates mail RH
export const getEmailTemplates = (structureId) => api.get('/hr/email-templates', { params: structureId ? { structureId } : {} });
export const createEmailTemplate = (data) => api.post('/hr/email-templates', data);
export const updateEmailTemplate = (id, data) => api.put(`/hr/email-templates/${id}`, data);
export const deleteEmailTemplate = (id) => api.delete(`/hr/email-templates/${id}`);

// Documents employé
export const uploadEmployeeDocument = (employeeId, formData) =>
  api.post(`/hr/employees/${employeeId}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
export const deleteEmployeeDocument = (docId) => api.delete(`/hr/employees/documents/${docId}`);
export const getEmployeeDocumentFile = (docId) =>
  api.get(`/hr/employees/documents/${docId}/file`, { responseType: 'blob' });
export const getAllEmployeeDocuments = (params) => api.get('/hr/employees/documents', { params });

// Salaires
export const getSalaryHistory = (employeeId) => api.get(`/hr/employees/${employeeId}/salaries`);
export const addSalary = (employeeId, data) => api.post(`/hr/employees/${employeeId}/salaries`, data);
export const simulateSalary = (employeeId, data) => api.post(`/hr/employees/${employeeId}/salaries/simulate`, data);

// Absences
export const getAbsences = (employeeId) => api.get(`/hr/employees/${employeeId}/absences`);
export const createAbsence = (employeeId, data) => api.post(`/hr/employees/${employeeId}/absences`, data);
export const validateAbsence = (id) => api.patch(`/hr/absences/${id}/validate`);
export const refuseAbsence = (id) => api.patch(`/hr/absences/${id}/refuse`);
export const sendAbsenceEmail = (id) => api.post(`/hr/absences/${id}/send-email`, {}, { timeout: 30000 });
export const uploadAbsenceDocument = (id, formData) =>
  api.patch(`/hr/absences/${id}/document`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
export const getAbsenceDocumentFile = (id) =>
  api.get(`/hr/absences/${id}/document/file`, { responseType: 'blob' });
export const deleteAbsence = (id) => api.delete(`/hr/absences/${id}`);
export const getAbsenceStats = () => api.get('/hr/absences/stats');

// Charges RH
export const getHRCosts = (params) => api.get('/hr/costs', { params });
export const upsertHRCost = (data) => api.post('/hr/costs', data);
export const deleteHRCost = (id) => api.delete(`/hr/costs/${id}`);

// Chiffre d'affaires
export const getRevenues = () => api.get('/hr/revenues');
export const upsertRevenue = (data) => api.post('/hr/revenues', data);

// Recrutement — Candidatures
export const getApplications = () => api.get('/hr/applications');
export const getApplicationById = (id) => api.get(`/hr/applications/${id}`);
export const createApplication = (data) => {
  const formData = new FormData();
  Object.entries(data).forEach(([k, v]) => { if (v !== undefined && v !== null) formData.append(k, v); });
  return api.post('/hr/applications', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const updateApplication = (id, data) => api.put(`/hr/applications/${id}`, data);
export const deleteApplication = (id) => api.delete(`/hr/applications/${id}`);
export const hireApplicant = (id, data) => api.post(`/hr/applications/${id}/hire`, data);
export const analyzeCV = (id, data = {}) => api.post(`/hr/applications/${id}/analyze-cv`, data, { timeout: 60000 });
export const validateEvaluation = (id, data) => api.put(`/hr/evaluations/${id}/validate`, data);

// Grilles d'évaluation
export const getEvaluationGrids = () => api.get('/hr/evaluation-grids');
export const createEvaluationGrid = (data) => api.post('/hr/evaluation-grids', data);
export const updateEvaluationGrid = (id, data) => api.put(`/hr/evaluation-grids/${id}`, data);
export const deleteEvaluationGrid = (id) => api.delete(`/hr/evaluation-grids/${id}`);

// Audit logs
export const getHRAuditLogs = () => api.get('/hr/audit-logs');

// Documents RH standalone
export const getHRDocuments = (params) => api.get('/hr/documents', { params });
export const getHRDocumentFile = (id) =>
  api.get(`/hr/documents/${id}/file`, { responseType: 'blob' });
export const uploadHRDocument = (formData) =>
  api.post('/hr/documents', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
export const deleteHRDocument = (id) => api.delete(`/hr/documents/${id}`);
export const reindexHRDocument = (id) => api.post(`/hr/documents/${id}/reindex`);

// Agent RH
export const hrAgentChat = (data) => api.post('/hr/agent/chat', data, { timeout: 90000 });
export const getHRConversations = () => api.get('/hr/agent/conversations');
export const getHRConversation = (id) => api.get(`/hr/agent/conversations/${id}`);
export const deleteHRConversation = (id) => api.delete(`/hr/agent/conversations/${id}`);
export const getHRAgentMemory = () => api.get('/hr/agent/memory');
export const addHRAgentMemory = (data) => api.post('/hr/agent/memory', data);
export const deleteHRAgentMemory = (id) => api.delete(`/hr/agent/memory/${id}`);

// Import RH (Meeko XLSX)
export const importHREmployees = (formData) =>
  api.post('/hr/import/employees', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const downloadHRImportTemplate = () =>
  api.get('/hr/import/template', { responseType: 'blob' });

// ============================================
// FINANCES
// ============================================

export const previewFinancialImport = (formData) =>
  api.post('/financial/import/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const importFinancialInvoices = (formData) =>
  api.post('/financial/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getFinancialDashboard = (structureIds, year, dateFrom, dateTo) =>
  api.get('/financial/dashboard', { params: { structureIds: structureIds?.join(','), year, dateFrom, dateTo } });
export const getFinancialImportBatches = () =>
  api.get('/financial/import-batches');
export const deleteFinancialImportBatch = (batchId) =>
  api.delete(`/financial/import-batches/${encodeURIComponent(batchId)}`);

// ============================================
// AGENT CYGNUS — SUPPORT APPLICATION
// ============================================

// Chat & Conversations
export const cygnusChat = (data) => api.post('/app-agent/chat', data, { timeout: 90000 });
export const getCygnusConversations = () => api.get('/app-agent/conversations');
export const getCygnusConversation = (id) => api.get(`/app-agent/conversations/${id}`);
export const deleteCygnusConversation = (id) => api.delete(`/app-agent/conversations/${id}`);

// Settings publics (tous utilisateurs auth)
export const getAppAgentPublicSettings = () => api.get('/app-agent/settings/public');

// Settings LLM (admin)
export const getCygnusSettings = () => api.get('/app-agent/settings');
export const upsertCygnusSettings = (data) => api.put('/app-agent/settings', data);
export const testCygnusConnection = () => api.post('/app-agent/settings/test');

// Mémoire (admin)
export const getCygnusMemory = () => api.get('/app-agent/memory');
export const addCygnusMemory = (data) => api.post('/app-agent/memory', data);
export const deleteCygnusMemory = (id) => api.delete(`/app-agent/memory/${id}`);

// Documents Markdown + PDF (admin)
export const getCygnusDocuments = () => api.get('/app-agent/documents');
export const getCygnusDocumentContent = (id) => api.get(`/app-agent/documents/${id}`);
export const createCygnusDocument = (data) => api.post('/app-agent/documents', data);
export const uploadCygnusPdf = (formData) => api.post('/app-agent/documents/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const updateCygnusDocument = (id, data) => api.put(`/app-agent/documents/${id}`, data);
export const deleteCygnusDocument = (id) => api.delete(`/app-agent/documents/${id}`);
export const reindexCygnusDocument = (id) => api.post(`/app-agent/documents/${id}/reindex`);

// ============================================
// AGENT LYRA — PROTOCOLES CRÈCHE
// ============================================

// Chat & Conversations
export const lyraChat = (data) => api.post('/lyra-agent/chat', data, { timeout: 90000 });
export const getLyraConversations = () => api.get('/lyra-agent/conversations');
export const getLyraConversation = (id) => api.get(`/lyra-agent/conversations/${id}`);
export const deleteLyraConversation = (id) => api.delete(`/lyra-agent/conversations/${id}`);

// Settings LLM (admin)
export const getLyraSettings = () => api.get('/lyra-agent/settings');
export const upsertLyraSettings = (data) => api.put('/lyra-agent/settings', data);
export const testLyraConnection = () => api.post('/lyra-agent/settings/test');

// Mémoire (admin)
export const getLyraMemory = () => api.get('/lyra-agent/memory');
export const addLyraMemory = (data) => api.post('/lyra-agent/memory', data);
export const deleteLyraMemory = (id) => api.delete(`/lyra-agent/memory/${id}`);

// Documents Markdown + PDF (admin)
export const getLyraDocuments = () => api.get('/lyra-agent/documents');
export const getLyraDocumentContent = (id) => api.get(`/lyra-agent/documents/${id}`);
export const createLyraDocument = (data) => api.post('/lyra-agent/documents', data);
export const uploadLyraPdf = (formData) => api.post('/lyra-agent/documents/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const updateLyraDocument = (id, data) => api.put(`/lyra-agent/documents/${id}`, data);
export const deleteLyraDocument = (id) => api.delete(`/lyra-agent/documents/${id}`);
export const reindexLyraDocument = (id) => api.post(`/lyra-agent/documents/${id}/reindex`);

// Documents en attente (Google Drive / N8N) — admin
export const getLyraPendingDocuments = () => api.get('/lyra-agent/pending');
export const approveLyraPendingDocument = (id) => api.put(`/lyra-agent/pending/${id}/approve`);
export const rejectLyraPendingDocument = (id) => api.delete(`/lyra-agent/pending/${id}`);

// ============================================
// NOVA — ORCHESTRATEUR
// ============================================

export const novaChat = (data) => api.post('/nova/chat', data, { timeout: 90000 });

// ============================================
// TRÉSORERIE
// ============================================
export const getTreasuryCategories = (params) => api.get('/treasury/categories', { params });
export const createTreasuryCategory = (data) => api.post('/treasury/categories', data);
export const updateTreasuryCategory = (id, data) => api.put(`/treasury/categories/${id}`, data);
export const deleteTreasuryCategory = (id) => api.delete(`/treasury/categories/${id}`);

export const getTreasuryBudgets = (params) => api.get('/treasury/budgets', { params });
export const upsertTreasuryBudget = (data) => api.post('/treasury/budgets', data);

export const getTreasuryTransactions = (params) => api.get('/treasury/transactions', { params });
export const createTreasuryTransaction = (data) => api.post('/treasury/transactions', data);
export const updateTreasuryTransaction = (id, data) => api.put(`/treasury/transactions/${id}`, data);
export const deleteTreasuryTransaction = (id) => api.delete(`/treasury/transactions/${id}`);

export const getTreasuryDashboard = (params) => api.get('/treasury/dashboard', { params });

export const treasuryImportPreview = (formData) => api.post('/treasury/import/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const treasuryImportConfirm = (data) => api.post('/treasury/import/confirm', data);
export const getTreasuryImportBatches = () => api.get('/treasury/import-batches');
export const deleteTreasuryImportBatch = (id) => api.delete(`/treasury/import-batches/${id}`);

// Treasury v2 — Dashboard summary, comptes, règles IA
export const getTreasuryDashboardSummary = (params) => api.get('/treasury/dashboard/summary', { params });
export const getTreasuryConsolidated = (params) => api.get('/treasury/consolidated', { params });
export const getTreasuryAccounts = (params) => api.get('/treasury/accounts', { params });
export const createTreasuryAccount = (data) => api.post('/treasury/accounts', data);
export const updateTreasuryAccount = (id, data) => api.put(`/treasury/accounts/${id}`, data);
export const deleteTreasuryAccount = (id) => api.delete(`/treasury/accounts/${id}`);
export const getTreasuryCategoryRules = (params) => api.get('/treasury/category-rules', { params });
export const createTreasuryCategoryRule = (data) => api.post('/treasury/category-rules', data);
export const deleteTreasuryCategoryRule = (id) => api.delete(`/treasury/category-rules/${id}`);
export const saveTreasuryCorrection = (data) => api.post('/treasury/category-rules/save-correction', data);

// Treasury Sprint 2 — Validation + Matching + Export iSuite
export const validateTreasuryTransaction = (id) => api.patch(`/treasury/transactions/${id}/validate`);
export const batchValidateTreasuryTransactions = (ids) => api.post('/treasury/transactions/batch-validate', { ids });
export const batchCategorizeTreasuryTransactions = (data) => api.post('/treasury/transactions/batch-categorize', data);
export const getTreasuryMatchingSuggestions = (transactionId) => api.get(`/treasury/matching/suggestions/${transactionId}`);
export const confirmTreasuryMatch = (data) => api.post('/treasury/matching/confirm', data);
export const unmatchTreasuryTransaction = (transactionId) => api.delete(`/treasury/matching/${transactionId}`);
export const exportTreasuryISuite = (params) => api.get('/treasury/export/isuite', { params, responseType: 'blob' });

// Treasury Sprint 3 — Factures fournisseurs en attente
export const getTreasuryPendingInvoices = (params) => api.get('/treasury/invoices', { params });
export const uploadTreasuryInvoice = (formData) => api.post('/treasury/invoices/upload', formData, {
  headers: { 'Content-Type': 'multipart/form-data' },
});
export const updateTreasuryInvoice = (id, data) => api.put(`/treasury/invoices/${id}`, data);
export const approveTreasuryInvoice = (id, data) => api.put(`/treasury/invoices/${id}/approve`, data);
export const rejectTreasuryInvoice = (id) => api.put(`/treasury/invoices/${id}/reject`);
export const deleteTreasuryInvoice = (id) => api.delete(`/treasury/invoices/${id}`);
export const getTreasuryInvoiceMatchingTransactions = (id) => api.get(`/treasury/invoices/${id}/matching`);

// Treasury Sprint 3 — Import PDF relevé bancaire (OCR LLM)
export const treasuryImportPdfPreview = (formData) => api.post('/treasury/import/pdf-preview', formData, {
  headers: { 'Content-Type': 'multipart/form-data' },
  timeout: 120000, // 2 min pour le LLM
});

// ============================================
// CRÈCHE — PLACES & CA
// ============================================
export const getCrecheChildren = (params) => api.get('/creche/children', { params });
export const createCrecheChild = (data) => api.post('/creche/children', data);
export const updateCrecheChild = (id, data) => api.put(`/creche/children/${id}`, data);
export const deleteCrecheChild = (id) => api.delete(`/creche/children/${id}`);

export const getCrecheContracts = (params) => api.get('/creche/contracts', { params });
export const createCrecheContract = (data) => api.post('/creche/contracts', data);
export const updateCrecheContract = (id, data) => api.put(`/creche/contracts/${id}`, data);
export const deleteCrecheContract = (id) => api.delete(`/creche/contracts/${id}`);

export const getCrecheTariffs = (params) => api.get('/creche/tariffs', { params });
export const createCrecheTariff = (data) => api.post('/creche/tariffs', data);
export const updateCrecheTariff = (id, data) => api.put(`/creche/tariffs/${id}`, data);
export const deleteCrecheTariff = (id) => api.delete(`/creche/tariffs/${id}`);

// Creche contract statuses
export const getCrecheContractStatuses = () => api.get('/creche-contract-statuses');
export const createCrecheContractStatus = (data) => api.post('/creche-contract-statuses', data);
export const updateCrecheContractStatus = (id, data) => api.put(`/creche-contract-statuses/${id}`, data);
export const deleteCrecheContractStatus = (id) => api.delete(`/creche-contract-statuses/${id}`);

export const getCrecheOccupancy = (params) => api.get('/creche/occupancy', { params });
export const getCrecheProjection = (params) => api.get('/creche/projection', { params });
export const getCrecheCapacity = (params) => api.get('/creche/capacity', { params });
export const upsertCrecheCapacity = (data) => api.put('/creche/capacity', data);
export const getCrecheMultiStructures = (params) => api.get('/creche/multi-structures', { params });

// Barèmes CMG
export const getCmgBaremes = () => api.get('/creche/cmg-baremes');
export const createCmgBareme = (data) => api.post('/creche/cmg-baremes', data);
export const updateCmgBareme = (id, data) => api.put(`/creche/cmg-baremes/${id}`, data);
export const deleteCmgBareme = (id) => api.delete(`/creche/cmg-baremes/${id}`);

// Simulations de régularisation
export const calculateRegularization = (data) => api.post('/creche/regularization/calculate', data);
export const getRegularizations = (params) => api.get('/creche/regularization', { params });
export const saveRegularization = (data) => api.post('/creche/regularization', data);
export const getRegularization = (id) => api.get(`/creche/regularization/${id}`);
export const deleteRegularization = (id) => api.delete(`/creche/regularization/${id}`);

// Documents familles
export const getFamilyDocumentTypes = () => api.get('/creche/family-document-types');
export const createFamilyDocumentType = (data) => api.post('/creche/family-document-types', data);
export const updateFamilyDocumentType = (id, data) => api.put(`/creche/family-document-types/${id}`, data);
export const deleteFamilyDocumentType = (id) => api.delete(`/creche/family-document-types/${id}`);
export const getFamilyDocuments = (params) => api.get('/creche/family-documents', { params });
export const getFamilyDocumentsMultiStructures = (params) => api.get('/creche/family-documents/multi-structures', { params });
export const updateFamilyDocumentCheck = (childId, docTypeId, data) => api.put(`/creche/family-documents/${childId}/${docTypeId}`, data);

// ============================================
// RH — PLANNING & FEUILLES DE TEMPS
// ============================================
export const getSchedules = (params) => api.get('/timesheets/schedules', { params });
export const upsertSchedule = (data) => api.post('/timesheets/schedules', data);
export const deleteSchedule = (id) => api.delete(`/timesheets/schedules/${id}`);
export const importSchedules = (data) => api.post('/timesheets/schedules/import', data);

export const getTimeSheets = (params) => api.get('/timesheets', { params });
export const getTimeSheetsMultiStructures = (structureIds) => api.get('/timesheets/multi-structures', { params: { structureIds: structureIds.join(',') } });
export const getTimeSheet = (id) => api.get(`/timesheets/${id}`);
export const createTimeSheet = (data) => api.post('/timesheets', data);
export const deleteTimeSheet = (id) => api.delete(`/timesheets/${id}`);
export const updateTimeSheetStatus = (id, status, label) => api.patch(`/timesheets/${id}/status`, { ...(status ? { status } : {}), ...(label ? { label } : {}) });
export const updateTimeSheetEntry = (id, data) => api.patch(`/timesheets/entries/${id}`, data);
export const updateTimeSheetVariable = (timesheetId, employeeId, data) => api.patch(`/timesheets/${timesheetId}/variables/${employeeId}`, data);
export const exportTimesheetCsv = (id) => api.get(`/timesheets/${id}/export`, { responseType: 'blob' });
export const regenerateTimesheetSchedule = (id, employeeId) => api.post(`/timesheets/${id}/regenerate-schedule`, { employeeId });
export const getRcrLedger = (employeeId) => api.get(`/timesheets/rcr/${employeeId}`);
export const adjustRcr = (employeeId, data) => api.post(`/timesheets/rcr/${employeeId}/adjust`, data);
export const getDebtLedger = (employeeId) => api.get(`/timesheets/debt/${employeeId}`);
export const adjustDebt = (employeeId, data) => api.post(`/timesheets/debt/${employeeId}/adjust`, data);
export const getTimesheetMotifs = () => api.get('/timesheets/motifs/list');
export const createTimesheetMotif = (data) => api.post('/timesheets/motifs', data);
export const updateTimesheetMotif = (id, data) => api.put(`/timesheets/motifs/${id}`, data);
export const deleteTimesheetMotif = (id) => api.delete(`/timesheets/motifs/${id}`);

// ============================================
// MODULE CALENDRIER
// ============================================
export const getCalendarCategories = () => api.get('/calendar/categories');
export const createCalendarCategory = (data) => api.post('/calendar/categories', data);
export const updateCalendarCategory = (id, data) => api.put(`/calendar/categories/${id}`, data);
export const deleteCalendarCategory = (id) => api.delete(`/calendar/categories/${id}`);
export const getCalendarEvents = (params) => api.get('/calendar/events', { params });
export const createCalendarEvent = (data) => api.post('/calendar/events', data);
export const updateCalendarEvent = (id, data) => api.put(`/calendar/events/${id}`, data);
export const deleteCalendarEvent = (id) => api.delete(`/calendar/events/${id}`);
export const createCalendarException = (id, data) => api.post(`/calendar/events/${id}/exception`, data);
export const getCalendarTasks = (params) => api.get('/calendar/tasks', { params });
export const completeTaskOccurrence = (taskId, data) => api.post(`/calendar/tasks/${taskId}/complete-occurrence`, data);

// ============================================
// RH — FICHES DE PAIE & DÉCLARATIONS
// ============================================
export const getSilaeDocuments = (params) => api.get('/hr/payroll/silae', { params });
export const uploadSilaeDocument = (formData) => api.post('/hr/payroll/silae/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deleteSilaeDocument = (id) => api.delete(`/hr/payroll/silae/${id}`);
export const markSilaeViewed = (id) => api.patch(`/hr/payroll/silae/${id}/viewed`);

export const getDeclarations = (params) => api.get('/hr/payroll/declarations', { params });
export const uploadDeclaration = (formData) => api.post('/hr/payroll/declarations/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const updateDeclarationAmount = (id, amount) => api.patch(`/hr/payroll/declarations/${id}`, { amount });
export const deleteDeclaration = (id) => api.delete(`/hr/payroll/declarations/${id}`);

// ============================================
// VEGA — AGENT PROACTIF
// ============================================
export const getVegaInsights = (params) => api.get('/vega/insights', { params });
export const getVegaInsightsSummary = () => api.get('/vega/insights/summary');
export const dismissVegaInsight = (id) => api.patch(`/vega/insights/${id}/dismiss`);
export const resolveVegaInsight = (id) => api.patch(`/vega/insights/${id}/resolve`);
export const dismissAllVegaInsights = (data) => api.post('/vega/insights/dismiss-all', data);
export const runVegaAnalysis = () => api.post('/vega/run', {}, { timeout: 120000 });
export const getVegaSettings = () => api.get('/vega/settings');
export const updateVegaSettings = (data) => api.put('/vega/settings', data);
export const testVegaConnection = () => api.post('/vega/settings/test');
export const getVegaMemories = () => api.get('/vega/memory');
export const addVegaMemory = (data) => api.post('/vega/memory', data);
export const deleteVegaMemory = (id) => api.delete(`/vega/memory/${id}`);
// Chat
export const vegaChat = (data) => api.post('/vega/chat', data, { timeout: 90000 });
export const getVegaConversations = () => api.get('/vega/conversations');
export const getVegaConversation = (id) => api.get(`/vega/conversations/${id}`);
export const deleteVegaConversation = (id) => api.delete(`/vega/conversations/${id}`);

// ============================================
// COUCHES — Suivi consommation
// ============================================
export const getDiapersDashboard = (params) => api.get('/diapers/dashboard', { params });
export const getDiapersComparison = (params) => api.get('/diapers/comparison', { params });
export const getDiapersProducts = (params) => api.get('/diapers/products', { params });
export const updateDiapersUnitsPerPackage = (id, data) => api.put(`/diapers/products/${id}/units`, data);
export const getDiapersConfig = () => api.get('/diapers/config');
export const updateDiapersConfig = (data) => api.put('/diapers/config', data);
export const getDiapersHistory = () => api.get('/diapers/history');
export const deleteDiapersHistoryBatch = (batch) => api.delete(`/diapers/history/batch/${batch}`);
export const getDiapersStructures = (params) => api.get('/diapers/structures', { params });

// ============================================
// RECRUTEMENT — Module standalone (v4.4.0)
// ============================================

// Offres d'emploi
export const getRecruitmentOffers = (params) => api.get('/recruitment/offers', { params });
export const getRecruitmentOffersMulti = (structureIds) => api.get('/recruitment/offers/multi-structures', { params: { structureIds } });
export const getRecruitmentOffer = (id) => api.get(`/recruitment/offers/${id}`);
export const createRecruitmentOffer = (data) => api.post('/recruitment/offers', data);
export const updateRecruitmentOffer = (id, data) => api.put(`/recruitment/offers/${id}`, data);
export const deleteRecruitmentOffer = (id) => api.delete(`/recruitment/offers/${id}`);

// Publications
export const createRecruitmentPublication = (offerId, data) => api.post(`/recruitment/offers/${offerId}/publications`, data);
export const updateRecruitmentPublication = (offerId, pubId, data) => api.put(`/recruitment/offers/${offerId}/publications/${pubId}`, data);
export const deleteRecruitmentPublication = (offerId, pubId) => api.delete(`/recruitment/offers/${offerId}/publications/${pubId}`);

// Candidatures
export const getRecruitmentApplications = (params) => api.get('/recruitment/applications', { params });
export const getRecruitmentApplicationsMulti = (structureIds) => api.get('/recruitment/applications/multi-structures', { params: { structureIds } });
export const getRecruitmentApplication = (id) => api.get(`/recruitment/applications/${id}`);
export const createRecruitmentApplication = (data) => {
  const formData = new FormData();
  Object.entries(data).forEach(([k, v]) => { if (v !== undefined && v !== null) formData.append(k, v); });
  return api.post('/recruitment/applications', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const updateRecruitmentApplication = (id, data) => api.put(`/recruitment/applications/${id}`, data);
export const deleteRecruitmentApplication = (id) => api.delete(`/recruitment/applications/${id}`);
export const updateRecruitmentApplicationStatus = (id, status) => api.patch(`/recruitment/applications/${id}/status`, { status });
export const uploadRecruitmentCV = (id, formData) => api.post(`/recruitment/applications/${id}/upload-cv`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const analyzeRecruitmentCV = (id, data) => api.post(`/recruitment/applications/${id}/analyze`, data, { timeout: 60000 });

// Commentaires
export const getRecruitmentComments = (id) => api.get(`/recruitment/applications/${id}/comments`);
export const createRecruitmentComment = (id, data) => api.post(`/recruitment/applications/${id}/comments`, data);
export const deleteRecruitmentComment = (commentId) => api.delete(`/recruitment/comments/${commentId}`);

// Entretiens
export const getRecruitmentInterviews = (params) => api.get('/recruitment/interviews', { params });
export const getRecruitmentInterviewsByApp = (id) => api.get(`/recruitment/applications/${id}/interviews`);
export const createRecruitmentInterview = (applicationId, data) => api.post(`/recruitment/applications/${applicationId}/interviews`, data);
export const updateRecruitmentInterview = (id, data) => api.put(`/recruitment/interviews/${id}`, data);
export const completeRecruitmentInterview = (id, data) => api.patch(`/recruitment/interviews/${id}/complete`, data);
export const deleteRecruitmentInterview = (id) => api.delete(`/recruitment/interviews/${id}`);

// Templates d'entretien
export const getInterviewTemplates = () => api.get('/recruitment/interview-templates');
export const createInterviewTemplate = (data) => api.post('/recruitment/interview-templates', data);
export const updateInterviewTemplate = (id, data) => api.put(`/recruitment/interview-templates/${id}`, data);
export const deleteInterviewTemplate = (id) => api.delete(`/recruitment/interview-templates/${id}`);

// CV N8N (queue admin)
export const getRecruitmentPendingCVs = () => api.get('/recruitment/cv/pending');
export const approveRecruitmentCV = (id, data) => api.put(`/recruitment/cv/pending/${id}/approve`, data);
export const rejectRecruitmentCV = (id) => api.delete(`/recruitment/cv/pending/${id}`);

// Dashboard
export const getRecruitmentDashboard = (params) => api.get('/recruitment/dashboard', { params });

// Postes administrés
export const getRecruitmentPositions = () => api.get('/recruitment/positions');
export const createRecruitmentPosition = (data) => api.post('/recruitment/positions', data);
export const updateRecruitmentPosition = (id, data) => api.put(`/recruitment/positions/${id}`, data);
export const deleteRecruitmentPosition = (id) => api.delete(`/recruitment/positions/${id}`);

// Meeko sync
export const getMeekoSettings = () => api.get('/meeko/settings');
export const updateMeekoSettings = (data) => api.put('/meeko/settings', data);
export const testMeekoConnection = () => api.post('/meeko/test');
export const previewMeekoSync = (data) => api.post('/meeko/sync/preview', data);
export const confirmMeekoSync = (data) => api.post('/meeko/sync/confirm', data);

// ==========================================
// Communication (Gazettes + Réseaux sociaux)
// ==========================================

// Settings (admin)
export const getCommunicationSettings = () => api.get('/communication/settings');
export const updateCommunicationSettings = (data) => api.put('/communication/settings', data);
export const testCommunicationLLM = () => api.post('/communication/settings/test-llm');

// Section Types (admin)
export const getGazetteSectionTypes = () => api.get('/communication/section-types');
export const createGazetteSectionType = (data) => api.post('/communication/section-types', data);
export const updateGazetteSectionType = (id, data) => api.put(`/communication/section-types/${id}`, data);
export const deleteGazetteSectionType = (id) => api.delete(`/communication/section-types/${id}`);
export const reorderGazetteSectionTypes = (order) => api.put('/communication/section-types/reorder', { order });

// Gazettes CRUD
export const getGazettes = (params) => api.get('/communication/gazettes', { params });
export const getGazettesMultiStructures = (structureIds, year) => api.get('/communication/gazettes/multi-structures', { params: { structureIds, year } });
export const getGazette = (id) => api.get(`/communication/gazettes/${id}`);
export const createGazette = (data) => api.post('/communication/gazettes', data);
export const updateGazette = (id, data) => api.put(`/communication/gazettes/${id}`, data);
export const deleteGazette = (id) => api.delete(`/communication/gazettes/${id}`);

// Sections
export const createGazetteSection = (gazetteId, data) => api.post(`/communication/gazettes/${gazetteId}/sections`, data);
export const updateGazetteSection = (gazetteId, sectionId, data) => api.put(`/communication/gazettes/${gazetteId}/sections/${sectionId}`, data);
export const deleteGazetteSection = (gazetteId, sectionId) => api.delete(`/communication/gazettes/${gazetteId}/sections/${sectionId}`);
export const reorderGazetteSections = (gazetteId, order) => api.put(`/communication/gazettes/${gazetteId}/sections/reorder`, { order });

// Section Items
export const createGazetteItem = (gazetteId, sectionId, data) => api.post(`/communication/gazettes/${gazetteId}/sections/${sectionId}/items`, data);
export const updateGazetteItem = (gazetteId, itemId, data) => api.put(`/communication/gazettes/${gazetteId}/items/${itemId}`, data);
export const deleteGazetteItem = (gazetteId, itemId) => api.delete(`/communication/gazettes/${gazetteId}/items/${itemId}`);

// Workflow
export const submitGazette = (id) => api.patch(`/communication/gazettes/${id}/submit`);
export const validateGazette = (id) => api.patch(`/communication/gazettes/${id}/validate`);
export const returnGazette = (id, comment) => api.patch(`/communication/gazettes/${id}/return`, { comment });
export const publishGazette = (id) => api.patch(`/communication/gazettes/${id}/publish`);

// Photos
export const uploadGazettePhotos = (gazetteId, formData) => api.post(`/communication/gazettes/${gazetteId}/photos`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const addGooglePhotosToGazette = (gazetteId, photos) => api.post(`/communication/gazettes/${gazetteId}/photos/google`, { photos });
export const updateGazettePhoto = (photoId, data) => api.put(`/communication/photos/${photoId}`, data);
export const deleteGazettePhoto = (photoId) => api.delete(`/communication/photos/${photoId}`);

// Google Photos (Picker API)
export const getGooglePhotosStatus = () => api.get('/communication/google-photos/status');
export const connectGooglePhotos = (structureKey) => api.post(`/communication/google-photos/connect/${structureKey}`);
export const disconnectGooglePhotos = (structureKey) => api.post(`/communication/google-photos/disconnect/${structureKey}`);
export const createGooglePickerSession = (structureKey) => api.post(`/communication/google-photos/${structureKey}/picker-session`);
export const pollGooglePickerSession = (structureKey, sessionId) => api.get(`/communication/google-photos/${structureKey}/picker-session/${sessionId}`);
export const importGooglePickerPhotos = (structureKey, sessionId, gazetteId) => api.post(`/communication/google-photos/${structureKey}/picker-session/${sessionId}/import`, { gazetteId });

// PDF & Preview
export const getGazettePdfUrl = (gazetteId) => `/api/communication/gazettes/${gazetteId}/pdf`;
export const getGazettePreviewUrl = (gazetteId) => `/api/communication/gazettes/${gazetteId}/preview`;
export const regenerateGazettePdf = (gazetteId) => api.post(`/communication/gazettes/${gazetteId}/regenerate-pdf`);

// IA (Agent Sirius)
export const generateGazetteFullText = (gazetteId) => api.post(`/communication/gazettes/${gazetteId}/generate-text`);
export const generateGazetteSectionText = (gazetteId, sectionId) => api.post(`/communication/gazettes/${gazetteId}/generate-section/${sectionId}`);

// Comments
export const getGazetteComments = (id) => api.get(`/communication/gazettes/${id}/comments`);
export const createGazetteComment = (id, data) => api.post(`/communication/gazettes/${id}/comments`, data);
export const deleteGazetteComment = (commentId) => api.delete(`/communication/comments/${commentId}`);

// ─── Fournisseurs transport ────────────────────────────────────────────────────
export const getTransportProviders = () => api.get('/hr/transport-providers');
export const createTransportProvider = (data) => api.post('/hr/transport-providers', data);
export const updateTransportProvider = (id, data) => api.put(`/hr/transport-providers/${id}`, data);
export const deleteTransportProvider = (id) => api.delete(`/hr/transport-providers/${id}`);

// ─── Types de formation ────────────────────────────────────────────────────────
export const getTrainingTypes = () => api.get('/hr/training-types');
export const createTrainingType = (data) => api.post('/hr/training-types', data);
export const updateTrainingType = (id, data) => api.put(`/hr/training-types/${id}`, data);
export const deleteTrainingType = (id) => api.delete(`/hr/training-types/${id}`);

// ─── Formations employé ────────────────────────────────────────────────────────
export const getEmployeeTrainings = (employeeId) => api.get(`/hr/employees/${employeeId}/trainings`);
export const createTraining = (employeeId, fd) => api.post(`/hr/employees/${employeeId}/trainings`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
export const updateTraining = (id, data) => api.put(`/hr/trainings/${id}`, data);
export const deleteTraining = (id) => api.delete(`/hr/trainings/${id}`);
export const uploadTrainingAttestation = (id, fd) => api.patch(`/hr/trainings/${id}/attestation`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getTrainingAttestation = (id) => api.get(`/hr/trainings/${id}/attestation`, { responseType: 'blob' });
export const getAllTrainings = (params) => api.get('/hr/trainings', { params });

// ─── Types entretien employé ───────────────────────────────────────────────────
export const getEmployeeInterviewTypes = () => api.get('/hr/employee-interview-types');
export const createEmployeeInterviewType = (data) => api.post('/hr/employee-interview-types', data);
export const updateEmployeeInterviewType = (id, data) => api.put(`/hr/employee-interview-types/${id}`, data);
export const deleteEmployeeInterviewType = (id) => api.delete(`/hr/employee-interview-types/${id}`);

// ─── Templates entretien employé ──────────────────────────────────────────────
export const getEmployeeInterviewTemplates = (params) => api.get('/hr/employee-interview-templates', { params });
export const createEmployeeInterviewTemplate = (data) => api.post('/hr/employee-interview-templates', data);
export const updateEmployeeInterviewTemplate = (id, data) => api.put(`/hr/employee-interview-templates/${id}`, data);
export const deleteEmployeeInterviewTemplate = (id) => api.delete(`/hr/employee-interview-templates/${id}`);

// ─── Entretiens employé ───────────────────────────────────────────────────────
export const getAllEmployeeInterviews = (params) => api.get('/hr/interviews', { params });
export const getEmployeeInterviews = (employeeId) => api.get(`/hr/employees/${employeeId}/interviews`);
export const createEmployeeInterview = (employeeId, data) => api.post(`/hr/employees/${employeeId}/interviews`, data);
export const saveInterviewAnswers = (id, data) => api.put(`/hr/interviews/${id}/answers`, data);
export const updateInterviewStatus = (id, status) => api.patch(`/hr/interviews/${id}/status`, { status });
export const updateInterviewScheduledDate = (id, scheduledDate) => api.patch(`/hr/interviews/${id}/scheduled-date`, { scheduledDate });
export const deleteEmployeeInterview = (id) => api.delete(`/hr/interviews/${id}`);

// ─── Types visites médicales ──────────────────────────────────────────────────
export const getMedicalVisitTypes = () => api.get('/hr/medical-visit-types');
export const createMedicalVisitType = (data) => api.post('/hr/medical-visit-types', data);
export const updateMedicalVisitType = (id, data) => api.put(`/hr/medical-visit-types/${id}`, data);
export const deleteMedicalVisitType = (id) => api.delete(`/hr/medical-visit-types/${id}`);

// ─── Templates email visites médicales ───────────────────────────────────────
export const getMedicalVisitEmailTemplates = () => api.get('/hr/medical-visit-email-templates');
export const saveMedicalVisitEmailTemplate = (data) => api.post('/hr/medical-visit-email-templates', data);
export const deleteMedicalVisitEmailTemplate = (typeId) => api.delete(`/hr/medical-visit-email-templates/${typeId}`);

// ─── Visites médicales employé ────────────────────────────────────────────────
export const getAllMedicalVisits = (params) => api.get('/hr/medical-visits', { params });
export const getMedicalVisitAlerts = (params) => api.get('/hr/medical-visits/alerts', { params });
export const getEmployeeMedicalVisits = (employeeId) => api.get(`/hr/employees/${employeeId}/medical-visits`);
export const createMedicalVisit = (employeeId, data) => api.post(`/hr/employees/${employeeId}/medical-visits`, data);
export const updateMedicalVisit = (employeeId, visitId, data) => api.put(`/hr/employees/${employeeId}/medical-visits/${visitId}`, data);
export const deleteMedicalVisit = (employeeId, visitId) => api.delete(`/hr/employees/${employeeId}/medical-visits/${visitId}`);
export const uploadMedicalVisitCr = (employeeId, visitId, file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.patch(`/hr/employees/${employeeId}/medical-visits/${visitId}/cr`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const deleteMedicalVisitCr = (employeeId, visitId) => api.delete(`/hr/employees/${employeeId}/medical-visits/${visitId}/cr`);
export const getMedicalVisitCrUrl = (employeeId, visitId) => `/api/hr/employees/${employeeId}/medical-visits/${visitId}/cr`;
export const getMedicalVisitCrFile = (employeeId, visitId) => api.get(`/hr/employees/${employeeId}/medical-visits/${visitId}/cr`, { responseType: 'blob' });
export const sendMedicalVisitRelance = (employeeId, visitId) => api.post(`/hr/employees/${employeeId}/medical-visits/${visitId}/relance`, {}, { timeout: 30000 });

// ─── Analytique RH ───────────────────────────────────────────────────────────
export const getHRAnalytics = (params) => api.get('/hr/analytics', { params });

// ─── iCal employé ─────────────────────────────────────────────────────────────
export const generateEmployeeIcalToken = (employeeId) => api.post(`/hr/employees/${employeeId}/ical-token`);
export const revokeEmployeeIcalToken = (employeeId) => api.post(`/hr/employees/${employeeId}/ical-token/revoke`);
export const getEmployeePlanningEntries = (employeeId, params) => api.get(`/hr/employees/${employeeId}/planning-entries`, { params });

// ─── Photo employé (organigramme) ─────────────────────────────────────────────
export const uploadEmployeePhoto = (employeeId, file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post(`/hr/employees/${employeeId}/photo`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const deleteEmployeePhoto = (employeeId) => api.delete(`/hr/employees/${employeeId}/photo`);
export const getEmployeePhotoBlob = (employeeId) => api.get(`/hr/employees/${employeeId}/photo`, { responseType: 'blob' });

// ─── Rôles organigramme ────────────────────────────────────────────────────────
export const getOrgRoles = () => api.get('/hr/org-roles');
export const createOrgRole = (data) => api.post('/hr/org-roles', data);
export const updateOrgRole = (id, data) => api.put(`/hr/org-roles/${id}`, data);
export const deleteOrgRole = (id) => api.delete(`/hr/org-roles/${id}`);

// ─── Périodes d'essai ────────────────────────────────────────────────────────
export const getTrialPeriods = (params) => api.get('/hr/trial-periods', { params });
export const getTrialPeriodAlerts = (params) => api.get('/hr/trial-periods/alerts', { params });
export const getEmployeeTrialPeriods = (employeeId) => api.get(`/hr/employees/${employeeId}/trial-periods`);
export const createTrialPeriod = (employeeId, data) => api.post(`/hr/employees/${employeeId}/trial-periods`, data);
export const updateTrialPeriod = (employeeId, id, data) => api.put(`/hr/employees/${employeeId}/trial-periods/${id}`, data);
export const confirmTrialPeriod = (employeeId, id) => api.patch(`/hr/employees/${employeeId}/trial-periods/${id}/confirm`);
export const ruptureTrialPeriod = (employeeId, id, motif) => api.patch(`/hr/employees/${employeeId}/trial-periods/${id}/rupture`, { motif });
export const deleteTrialPeriod = (employeeId, id) => api.delete(`/hr/employees/${employeeId}/trial-periods/${id}`);

// ─── Groupes organigramme ─────────────────────────────────────────────────────
export const getOrgGroups = () => api.get('/hr/org-groups');
export const createOrgGroup = (data) => api.post('/hr/org-groups', data);
export const updateOrgGroup = (id, data) => api.put(`/hr/org-groups/${id}`, data);
export const deleteOrgGroup = (id) => api.delete(`/hr/org-groups/${id}`);

// ─── Plans de planning globaux (SchedulePlan par structure) ──────────────────
export const getSchedulePlans = (structureId) => api.get('/hr/schedule-plans', { params: { structureId } });
export const initSchedulePlans = (structureId) => api.post('/hr/schedule-plans/init', { structureId });
export const createSchedulePlan = (data) => api.post('/hr/schedule-plans', data);
export const updateSchedulePlan = (planId, data) => api.put(`/hr/schedule-plans/${planId}`, data);
export const deleteSchedulePlan = (planId) => api.delete(`/hr/schedule-plans/${planId}`);
export const duplicateSchedulePlan = (planId, data) => api.post(`/hr/schedule-plans/${planId}/duplicate`, data);
export const duplicateSchedulePlanWeek = (planId, data) => api.post(`/hr/schedule-plans/${planId}/duplicate-week`, data);
export const validateSchedulePlan = (planId) => api.patch(`/hr/schedule-plans/${planId}/validate`);
export const unvalidateSchedulePlan = (planId) => api.patch(`/hr/schedule-plans/${planId}/unvalidate`);
export const upsertSchedulePlanSlot = (planId, data) => api.post(`/hr/schedule-plans/${planId}/slots`, data);
export const deleteSchedulePlanSlot = (planId, slotId) => api.delete(`/hr/schedule-plans/${planId}/slots/${slotId}`);

// ─── Recherche globale multi-modules ────────────────────────────────
export const globalSearch = (q) => api.get('/search', { params: { q } });

// ─── Types de documents employé (admin CRUD) ─────────────────────────
export const getEmployeeDocumentTypes = () => api.get('/hr/employee-document-types');
export const createEmployeeDocumentType = (data) => api.post('/hr/employee-document-types', data);
export const updateEmployeeDocumentType = (id, data) => api.put(`/hr/employee-document-types/${id}`, data);
export const deleteEmployeeDocumentType = (id) => api.delete(`/hr/employee-document-types/${id}`);

// ─── Recrutement : conversion candidature → employé (admin) ──────────
export const convertApplicationToEmployee = (id, overrides = {}) =>
  api.post(`/recruitment/applications/${id}/convert-to-employee`, overrides);

// ─── RH : promesse d'embauche (admin) ────────────────────────────────
// Timeout 60s sur la génération : Puppeteer (lancement Chrome + render + PDF) peut prendre 10-15s à froid
export const previewOfferLetter = (employeeId, payload = {}) =>
  api.post(`/hr/employees/${employeeId}/preview-offer-letter`, payload);
export const generateOfferLetter = (employeeId, payload = {}) =>
  api.post(`/hr/employees/${employeeId}/generate-offer-letter`, payload, { timeout: 60000 });

// ─── RH : demande de documents (P5 — admin) ───────────────────────────
export const listDocumentRequests = (employeeId) =>
  api.get('/hr/document-requests', { params: employeeId ? { employeeId } : {} });
export const getDocumentRequest = (id) =>
  api.get(`/hr/document-requests/${id}`);
export const createDocumentRequest = (payload) =>
  api.post('/hr/document-requests', payload);
export const updateDocumentRequest = (id, patch) =>
  api.patch(`/hr/document-requests/${id}`, patch);
export const deleteDocumentRequest = (id) =>
  api.delete(`/hr/document-requests/${id}`);
export const resendDocumentRequest = (id) =>
  api.post(`/hr/document-requests/${id}/resend`);
export const regenerateDocumentRequestLink = (id) =>
  api.post(`/hr/document-requests/${id}/regenerate-link`);
export const updateDocumentRequestItem = (itemId, payload) =>
  api.patch(`/hr/document-requests/items/${itemId}`, payload);
export const addDocumentRequestItems = (id, payload) =>
  api.post(`/hr/document-requests/${id}/items`, payload);
export const promoteDocumentRequestItem = (itemId, payload = {}) =>
  api.post(`/hr/document-requests/items/${itemId}/promote-to-type`, payload);
export const getDocumentRequestEmailPresets = (employeeId) =>
  api.get('/hr/document-requests/email-presets', { params: { employeeId } });

// ─── RH : collecte mensuelle justificatifs de transport (admin) ────────
export const getTransportCollectionCandidates = (period, structureIds = null) =>
  api.get('/hr/transport-collection/candidates', {
    params: { period, structureIds: structureIds || undefined },
  });
export const launchTransportCollectionCampaign = (payload) =>
  api.post('/hr/transport-collection/launch', payload);
export const setEmployeeDefaultTransportProvider = (employeeId, transportProviderId) =>
  api.put(`/hr/transport-collection/employee/${employeeId}/default-provider`, { transportProviderId });

// ─── RH : Modèles de documents (Promesse, CDI, CDD, ...) ──────────────
export const getDocumentTemplates = (params = {}) =>
  api.get('/hr/document-templates', { params });
export const getDocumentTemplateById = (id) =>
  api.get(`/hr/document-templates/${id}`);
export const createDocumentTemplate = (payload) =>
  api.post('/hr/document-templates', payload);
export const updateDocumentTemplate = (id, payload) =>
  api.put(`/hr/document-templates/${id}`, payload);
export const deleteDocumentTemplate = (id) =>
  api.delete(`/hr/document-templates/${id}`);
export const duplicateDocumentTemplate = (id) =>
  api.post(`/hr/document-templates/${id}/duplicate`);
export const getDocumentTemplateVariables = () =>
  api.get('/hr/document-templates-variables');
export const previewDocumentTemplate = (payload) =>
  api.post('/hr/document-templates/preview', payload, { responseType: 'text' });
export const generateEmployeeDocument = (employeeId, payload) =>
  api.post(`/hr/employees/${employeeId}/generate-document`, payload, { responseType: 'blob' });

// ─── Crèche : Module Repas (factures GOÛT'CHOU) ──────────────────────
export const getMealsDashboard = (params = {}) =>
  api.get('/creche/meals/dashboard', { params });
export const getMealsComparison = (params = {}) =>
  api.get('/creche/meals/comparison', { params });
export const getMealInvoices = (params = {}) =>
  api.get('/creche/meals/invoices', { params });
export const getMealInvoiceById = (id) =>
  api.get(`/creche/meals/invoices/${id}`);
export const downloadMealInvoice = (id) =>
  api.get(`/creche/meals/invoices/${id}/download`, { responseType: 'blob' });
export const deleteMealInvoice = (id) =>
  api.delete(`/creche/meals/invoices/${id}`);
export const previewMealInvoice = (formData) =>
  api.post('/creche/meals/upload-preview', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
export const confirmMealInvoice = (payload) =>
  api.post('/creche/meals/upload-confirm', payload);
export const getMealProductTypes = () =>
  api.get('/creche/meals/product-types');
export const createMealProductType = (payload) =>
  api.post('/creche/meals/product-types', payload);
export const updateMealProductType = (id, payload) =>
  api.put(`/creche/meals/product-types/${id}`, payload);
export const deleteMealProductType = (id) =>
  api.delete(`/creche/meals/product-types/${id}`);

export default api;
